// src/components/books/BookCardModal.jsx
import React, { useEffect, useCallback, useState } from "react";
import { API_BASE } from "../../utils/api"
import "../../styles/BookCardModal.css";

export default function BookCardModal({
  open,
  book,
  onClose,
  onPrimary,
  primaryLabel,
  context = "library",
}) {
  const [owned, setOwned] = useState(false);
  const [checking, setChecking] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const accessToken = localStorage.getItem("rv_access_token");

  const getUserId = () => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;
      const u = JSON.parse(raw);

      const pick = (v) => {
        if (!v) return null;
        if (typeof v === "string") return v;
        if (v.$oid) return v.$oid;
        return null;
      };

      return (
        pick(u?._id) ||
        pick(u?.id) ||
        localStorage.getItem("userId") ||
        null
      );
    } catch {
      return null;
    }
  };

  const escHandler = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", escHandler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", escHandler);
      document.body.style.overflow = prev;
    };
  }, [open, escHandler]);

  const id = book?._id || book?.id;

  // Ownership check
  useEffect(() => {
    let alive = true;
    if (!open || !id) return;

    const check = async () => {
      if (context !== "library") {
        setOwned(true);
        setChecking(false);
        return;
      }
      try {
        setChecking(true);
        const headers = accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : {};
        const res = await fetch(`${API_BASE}/api/ownership/${id}`, {
          credentials: "omit",
          headers,
        });
        const data = await res.json().catch(() => ({}));
        if (alive) setOwned(!!data.owned);
      } catch {
        if (alive) setOwned(false);
      } finally {
        if (alive) setChecking(false);
      }
    };

    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [open, id, context, accessToken]);

  // Wishlist check
  useEffect(() => {
    let alive = true;
    if (!open || !id || !accessToken) return;

    const checkWishlist = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wishlist/check/${id}`, {
          credentials: "omit",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (alive) setInWishlist(!!data.inWishlist);
      } catch {
        if (alive) setInWishlist(false);
      }
    };

    checkWishlist();
    return () => { alive = false; };
  }, [open, id, accessToken]);

  // Primary action:
  // - owned → open reader
  // - not owned (library) → Stripe checkout
  const handlePrimary = async () => {
    if (!book || !id) return;

    if (typeof onPrimary === "function") {
      onPrimary();
      return;
    }

    if (owned || context !== "library") {
      window.location.assign(`/read/${id}?type=book`);
      return;
    }

    if (!accessToken) {
      alert("Please sign in to purchase this book.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/purchases/checkout`, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ bookId: id }),
      });
      const data = await res.json();
      if (data?.alreadyOwned) {
        setOwned(true);
        window.location.assign(`/read/${id}?type=book`);
        return;
      }
      if (!res.ok || !data?.url) throw new Error("checkout failed");
      window.location.assign(data.url);
    } catch {
      alert("Could not start checkout. Please try again.");
    }
  };

  const handleWishlist = async () => {
    if (!accessToken) {
      alert("Please sign in to add books to your wishlist.");
      return;
    }

    if (!id) return;

    setWishlistLoading(true);

    try {
      if (inWishlist) {
        // Remove from wishlist
        const res = await fetch(`${API_BASE}/api/wishlist/${id}`, {
          method: "DELETE",
          credentials: "omit",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (res.ok) {
          setInWishlist(false);
        } else {
          throw new Error("Failed to remove from wishlist");
        }
      } else {
        // Add to wishlist
        const res = await fetch(`${API_BASE}/api/wishlist/${id}`, {
          method: "POST",
          credentials: "omit",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await res.json();
        
        if (data?.alreadyInWishlist || res.ok) {
          setInWishlist(true);
        } else if (data?.error === "already_owned") {
          alert("You already own this book!");
        } else {
          throw new Error("Failed to add to wishlist");
        }
      }
    } catch (error) {
      alert(`Could not update wishlist. ${error.message}`);
    } finally {
      setWishlistLoading(false);
    }
  };

  const computedLabel =
    checking ? "Checking…" : owned || context !== "library" ? "Open" : "Buy";
  const finalLabel = primaryLabel ?? computedLabel;

  if (!open || !book) return null;

  const fallbackBlurb =
    "No blurb yet — the author hasn't added a description.";
  const blurb = (book.blurb || "").trim() || fallbackBlurb;

  return (
    <div
      className="bcm-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bcm-modal" role="dialog" aria-modal="true">
        <button className="bcm-closeX" aria-label="Close" onClick={onClose}>
          ×
        </button>

        <div className="bcm-header">
          <h2 className="bcm-title">{book.title}</h2>
          <div className="bcm-author">
            {book.authorName || book.author || ""}
          </div>
          {book.genre ? <span className="bcm-chip">{book.genre}</span> : null}
        </div>

        <div className="bcm-body">
          <div className="bcm-blurbBlock">
            <div className="bcm-blurbLabel">Blurb</div>
            <div>{blurb}</div>
          </div>
        </div>

        <div className="bcm-footer">
          <button className="bcm-btn" onClick={onClose}>
            Close
          </button>
          {!owned && context === "library" && (
            <button
              className="bcm-btnWishlist"
              onClick={handleWishlist}
              disabled={wishlistLoading}
            >
              {wishlistLoading
                ? "..."
                : inWishlist
                ? "Remove from Wishlist"
                : "Add to Wishlist"}
            </button>
          )}
          <button
            className="bcm-btnPrimary"
            onClick={handlePrimary}
            disabled={checking}
          >
            {finalLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
